import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, Warning, ArrowsClockwise, Link as LinkIcon, Plug, PlugsConnected, Stethoscope } from "@phosphor-icons/react";
import HintButton from "@/components/HintButton";
import SyncDiagnosticPanel from "@/components/bling/SyncDiagnosticPanel";

export default function BlingSettings() {
  const [cfg, setCfg] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [redirectUri, setRedirectUri] = useState("");
  const [redirectUriAlt, setRedirectUriAlt] = useState("");
  const [saving, setSaving] = useState(false);
  const [params] = useSearchParams();
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [situacoes, setSituacoes] = useState([]);
  const [enviadoSituacaoId, setEnviadoSituacaoId] = useState("");
  const [savingSituacao, setSavingSituacao] = useState(false);
  const [statusPushes, setStatusPushes] = useState([]);
  const [cutoffDate, setCutoffDate] = useState("");
  const [savingCutoff, setSavingCutoff] = useState(false);
  const [expectedRedirect, setExpectedRedirect] = useState(null);
  const [redirectMatches, setRedirectMatches] = useState(null);
  const [redirectCopied, setRedirectCopied] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [authMode, setAuthMode] = useState("opaque");
  const [savingMode, setSavingMode] = useState(false);

  const saveAuthMode = async () => {
    const warn = cfg.connected
      ? "A troca renova o token do Bling IMEDIATAMENTE no novo modo.\n\nSe a renovação falhar, o modo anterior é mantido e a conexão preservada.\n\nContinuar?"
      : "Sem conexão ativa no momento: o modo será salvo e usado automaticamente na próxima autorização OAuth.\n\nContinuar?";
    if (!window.confirm(warn)) return;
    setSavingMode(true);
    try {
      const { data } = await api.put("/bling/auth-mode", { mode: authMode });
      setMessage(data.detail || (data.ok ? "Modo de autenticação salvo." : "Falha ao trocar o modo."));
      setMessageType(data.ok ? "success" : "error");
      await load();
    } catch (e) {
      if (e?.response?.status !== 428) {
        setMessage(e?.response?.data?.detail || "Erro ao salvar o modo de autenticação");
        setMessageType("error");
      }
    } finally { setSavingMode(false); }
  };

  const runDiagnostic = async () => {
    setDiagLoading(true);
    setDiag(null);
    try {
      const { data } = await api.get("/bling/sync-diagnostic", { timeout: 90000 });
      setDiag(data);
    } catch (e) {
      const st = e?.response?.status;
      const det = e?.response?.data?.detail;
      setMessage(
        det
          ? `Diagnóstico falhou (HTTP ${st}): ${det}`
          : `Diagnóstico falhou (HTTP ${st || "sem resposta"}). Se persistir, verifique regras de segurança/WAF da Cloudflare do domínio para caminhos /api/.`
      );
      setMessageType("error");
    } finally { setDiagLoading(false); }
  };

  useEffect(() => {
    const status = params.get("status");
    if (status === "connected") {
      setMessage("Conta Bling conectada com sucesso! Sincronização iniciada em background.");
      setMessageType("success");
    } else if (status === "error") {
      setMessage(`Erro: ${params.get("msg") || "falha desconhecida"}`);
      setMessageType("error");
    }
  }, [params]);

  const load = async () => {
    const { data } = await api.get("/bling/configuration");
    setCfg(data);
    setClientId(data.client_id || "");
    setAuthMode(data.auth_mode || "opaque");
    setRedirectUri(data.redirect_uri || `${window.location.origin}/api/bling/callback`);
    setRedirectUriAlt(data.redirect_uri_alt || "");
    setEnviadoSituacaoId(data.enviado_situacao_id ? String(data.enviado_situacao_id) : "");
    setCutoffDate(data.sync_cutoff_date || "");
    try {
      const { data: wi } = await api.get("/bling/webhook-info");
      setWebhookInfo(wi);
    } catch (e) { console.warn("webhook-info indisponível:", e?.response?.status); }
    try {
      const { data: sit } = await api.get("/bling/situacoes");
      setSituacoes(sit || []);
    } catch (e) { console.warn("situacoes indisponíveis (Bling desconectado?):", e?.response?.status); }
    try {
      const { data: pushes } = await api.get("/bling/status-pushes");
      setStatusPushes(pushes || []);
    } catch (e) { console.warn("status-pushes indisponíveis:", e?.response?.status); }
    try {
      const { data: rd } = await api.get("/bling/oauth/expected-redirect");
      setExpectedRedirect(rd.expected_redirect_uri);
      setRedirectMatches(rd.matches);
    } catch (e) { console.warn("expected-redirect indisponível:", e?.response?.status); }
  };

  const saveEnviadoSituacao = async () => {
    setSavingSituacao(true);
    try {
      await api.put("/bling/enviado-situacao", {
        situacao_id: enviadoSituacaoId ? parseInt(enviadoSituacaoId, 10) : null,
      });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSavingSituacao(false);
    }
  };

  const saveCutoffDate = async () => {
    setSavingCutoff(true);
    try {
      await api.put("/bling/sync-cutoff-date", { sync_cutoff_date: cutoffDate });
      setMessage(cutoffDate ? `Data de corte atualizada: pedidos com emissão a partir de ${cutoffDate} serão sincronizados.` : "Filtro de data removido — todos os pedidos do Bling serão sincronizados.");
      setMessageType("success");
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro ao salvar data de corte");
    } finally {
      setSavingCutoff(false);
    }
  };

  const copyWebhookUrl = async () => {
    if (!webhookInfo?.webhook_url) return;
    try {
      await navigator.clipboard.writeText(webhookInfo.webhook_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { console.warn("Falha ao copiar URL para clipboard:", e?.message); }
  };

  const copyRedirectUri = async () => {
    if (!redirectUri) return;
    try {
      await navigator.clipboard.writeText(redirectUri);
      setRedirectCopied(true);
      setTimeout(() => setRedirectCopied(false), 2000);
    } catch (e) { console.warn("Falha ao copiar Redirect URI:", e?.message); }
  };

  const useExpectedRedirect = () => {
    if (expectedRedirect) setRedirectUri(expectedRedirect);
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await api.post("/bling/configuration", {
        client_id: clientId,
        client_secret: clientSecret,
        redirect_uri: redirectUri,
        redirect_uri_alt: redirectUriAlt,
      });
      setMessage("Configuração salva.");
      setMessageType("success");
      setClientSecret("");
      load();
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Erro ao salvar");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  const connect = async (useAlt = false) => {
    try {
      const { data } = await api.get("/bling/oauth/authorize-url", { params: useAlt ? { use_alt: true } : {} });
      window.location.href = data.url;
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Erro");
      setMessageType("error");
    }
  };

  const disconnect = async () => {
    if (!confirm("Desconectar a conta Bling?")) return;
    await api.post("/bling/disconnect");
    load();
  };

  const syncNow = async () => {
    try {
      await api.post("/bling/sync-now");
      setMessage("Sincronização iniciada em background.");
      setMessageType("success");
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Erro");
      setMessageType("error");
    }
  };

  const refetchMissing = async () => {
    try {
      const { data } = await api.post("/orders/refetch-missing-items");
      setMessage(data.message || "Refetch iniciado em background.");
      setMessageType("success");
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Erro");
      setMessageType("error");
    }
  };

  if (!cfg) return <div className="p-12">Carregando…</div>;

  return (
    <div className="p-6 md:p-10 max-w-3xl">
      <div className="mb-8">
        <div className="overline text-zinc-500 mb-2">INTEGRAÇÃO</div>
        <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Bling ERP</h1>
        <p className="text-zinc-600 mt-3">
          Conecte sua conta Bling para importar pedidos, produtos, clientes e categorias automaticamente.
        </p>
      </div>

      {message && (
        <div className={`border p-4 mb-6 flex items-center gap-3 ${messageType === "success" ? "border-[#00A300] bg-green-50 text-[#00A300]" : "border-[#E60000] bg-red-50 text-[#E60000]"}`} data-testid="bling-message">
          {messageType === "success" ? <CheckCircle size={20} weight="bold" /> : <Warning size={20} weight="bold" />}
          <div>{message}</div>
        </div>
      )}

      {/* Status */}
      <div className="border border-black p-6 mb-6 bg-black text-white" data-testid="bling-status">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {cfg.connected ? <PlugsConnected size={32} weight="bold" color="#00A300" /> : <Plug size={32} weight="bold" color="#FFC000" />}
            <div>
              <div className="overline text-white/60">STATUS</div>
              <div className="font-head text-2xl font-bold">
                {cfg.connected ? "Conectado" : "Desconectado"}
              </div>
            </div>
          </div>
          <div className="text-right text-xs">
            {cfg.last_orders_sync_at && (
              <>
                <div className="overline text-white/60">ÚLTIMA SYNC</div>
                <div className="font-mono mt-1">{new Date(cfg.last_orders_sync_at).toLocaleString("pt-BR")}</div>
              </>
            )}
          </div>
        </div>

        <div className="mt-6 flex gap-2 flex-wrap">
          {!cfg.connected && (
            <>
              <button onClick={() => connect(false)} disabled={!cfg.client_id || !redirectUri} className="btn" style={{ background: "#00A300", borderColor: "#00A300" }} data-testid="connect-bling">
                <PlugsConnected size={16} weight="bold" /> Conectar (URL principal)
              </button>
              {redirectUriAlt && (
                <button onClick={() => connect(true)} disabled={!cfg.client_id} className="btn" style={{ background: "#00509E", borderColor: "#00509E" }} data-testid="connect-bling-alt">
                  <PlugsConnected size={16} weight="bold" /> Conectar (URL secundária)
                </button>
              )}
            </>
          )}
          {cfg.connected && (
            <>
              <button onClick={syncNow} className="btn btn-klein" data-testid="sync-now">
                <ArrowsClockwise size={16} weight="bold" /> Sincronizar Agora
              </button>
              <button onClick={refetchMissing} className="btn btn-ghost" style={{background:"#fff",color:"#000"}} data-testid="refetch-missing">
                <ArrowsClockwise size={16} weight="bold" /> Re-importar Itens Faltantes
              </button>
              <button onClick={disconnect} className="btn btn-danger" data-testid="disconnect-bling">Desconectar</button>
            </>
          )}
          <button
            onClick={runDiagnostic}
            disabled={diagLoading}
            className="btn btn-ghost"
            style={{ background: "#fff", color: "#000" }}
            data-testid="sync-diagnostic-btn"
            title="Consulta a 1ª página de pedidos no Bling e mostra, pedido a pedido, o que o sync faria — sem alterar nada"
          >
            <Stethoscope size={16} weight="bold" className={diagLoading ? "animate-pulse" : ""} />
            {diagLoading ? "Diagnosticando... (até 1 min)" : "Diagnóstico do Sync"}
          </button>
        </div>
      </div>

      <SyncDiagnosticPanel diag={diag} />

      {/* Modo de autenticação (Tradicional vs JWT) */}
      <div className="border border-black p-6 mb-6 bg-white" data-testid="bling-auth-mode-card">
        <div className="overline text-zinc-500 mb-1">MODO DE AUTENTICAÇÃO</div>
        <h2 className="font-head text-xl font-bold mb-2">Tradicional ou JWT</h2>
        <p className="text-sm text-zinc-600 mb-4" data-testid="auth-mode-current">
          Modo salvo: <b>{cfg.auth_mode === "jwt" ? "JWT — Novo padrão" : "Tradicional — Token opaco"}</b>
          <span className="mx-2 text-zinc-300">·</span>
          Conexão: <b className={cfg.connected ? "text-green-700" : "text-[#E60000]"}>{cfg.connected ? "ativa" : "desconectada"}</b>
          {cfg.token_format && (
            <>
              <span className="mx-2 text-zinc-300">·</span>
              Formato do token em uso: <b className="uppercase">{cfg.token_format}</b>
            </>
          )}
        </p>
        <div className="space-y-3 mb-5">
          <label className="flex items-start gap-3 cursor-pointer border border-zinc-200 p-3 hover:border-black" data-testid="auth-mode-opaque">
            <input type="radio" name="bling_auth_mode" className="mt-1" checked={authMode === "opaque"} onChange={() => setAuthMode("opaque")} />
            <span>
              <b>Tradicional — Token opaco</b>
              <span className="block text-xs text-zinc-500 mt-0.5">Padrão atual. Não envia o header enable-jwt. Funciona somente enquanto o Bling aceitar tokens opacos.</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer border border-zinc-200 p-3 hover:border-black" data-testid="auth-mode-jwt">
            <input type="radio" name="bling_auth_mode" className="mt-1" checked={authMode === "jwt"} onChange={() => setAuthMode("jwt")} />
            <span>
              <b>JWT — Novo padrão</b>
              <span className="block text-xs text-zinc-500 mt-0.5">Envia enable-jwt: 1 na obtenção/renovação de tokens e em todas as chamadas à API. A troca renova o token na hora; se falhar, o modo anterior é mantido.</span>
            </span>
          </label>
        </div>
        <button
          onClick={saveAuthMode}
          disabled={savingMode || authMode === (cfg.auth_mode || "opaque")}
          className="btn btn-klein"
          data-testid="save-auth-mode"
        >
          {savingMode ? "Salvando..." : "Salvar modo de autenticação"}
        </button>
      </div>

      {/* Configuration */}
      <form onSubmit={save} className="border border-black p-6 md:p-8 bg-white space-y-5" data-testid="bling-config-form">
        <div>
          <div className="overline text-zinc-500 mb-2">CREDENCIAIS DO APP BLING</div>
          <h2 className="font-head text-2xl font-bold">Configuração</h2>
          <HintButton text="Crie um app em{&quot; &quot;} developer.bling.com.br/aplicativos {&quot; &quot;} e cole as credenciais aqui." />
        </div>

        {cfg.credentials_source === "env" && (
          <div className="border border-green-600 bg-green-50 p-3 text-sm flex items-start gap-2" data-testid="bling-creds-from-env">
            <span className="font-mono text-green-700 shrink-0">🔒</span>
            <div>
              <b>Client ID e Client Secret carregados de variáveis de ambiente</b> (<code className="text-xs">BLING_CLIENT_ID</code> / <code className="text-xs">BLING_CLIENT_SECRET</code> no <code>backend/.env</code>). Para trocar, edite o <code>.env</code> e reinicie o backend — os campos abaixo estão bloqueados propositalmente.
            </div>
          </div>
        )}

        <div>
          <label className="label">Client ID {cfg.credentials_source === "env" && <span className="text-xs text-green-700">(via .env)</span>}</label>
          <input
            className="input font-mono"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="ex: 1234567890abc..."
            disabled={cfg.credentials_source === "env"}
            data-testid="bling-client-id"
          />
        </div>

        <div>
          <label className="label">Client Secret {cfg.credentials_source === "env" && <span className="text-xs text-green-700">(via .env)</span>}</label>
          <input
            className="input font-mono"
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={cfg.credentials_source === "env" ? "•••••••• (definido em BLING_CLIENT_SECRET)" : (cfg.has_secret ? "•••••••• (mantém o valor salvo se vazio)" : "Cole o Client Secret")}
            disabled={cfg.credentials_source === "env"}
            data-testid="bling-client-secret"
          />
        </div>

        <div>
          <label className="label">Redirect URI (Link de Redirecionamento)</label>
          <div className="flex items-stretch gap-2">
            <input className="input font-mono text-xs flex-1" value={redirectUri} onChange={(e) => setRedirectUri(e.target.value)} data-testid="bling-redirect-uri" />
            <button type="button" onClick={copyRedirectUri} className="btn btn-ghost px-4" data-testid="copy-redirect-uri">
              {redirectCopied ? "COPIADO!" : "COPIAR"}
            </button>
          </div>
          {expectedRedirect && redirectMatches === false && (
            <div className="mt-2 border border-[#FFC000] bg-yellow-50 p-3 text-xs" data-testid="redirect-mismatch-warn">
              <div className="font-bold text-[#B45309] mb-1 flex items-center gap-1.5">
                <Warning size={14} weight="bold" /> Atenção: URL não bate com o ambiente atual
              </div>
              <div className="text-zinc-700 mb-2">Este navegador está em <code className="font-mono bg-white px-1">{expectedRedirect}</code>. Se o Bling redirecionar pra cá, ele precisa dessa URL cadastrada.</div>
              <button type="button" onClick={useExpectedRedirect} className="btn btn-ghost btn-tight text-xs" data-testid="use-expected-redirect">
                Usar URL deste ambiente
              </button>
            </div>
          )}
          {expectedRedirect && redirectMatches === true && (
            <div className="mt-2 text-xs text-[#00A300] font-semibold flex items-center gap-1.5" data-testid="redirect-match-ok">
              <CheckCircle size={14} weight="bold" /> URL bate com este ambiente
            </div>
          )}
          <div className="text-xs text-zinc-500 mt-2 flex items-start gap-2">
            <LinkIcon size={14} className="mt-0.5 flex-shrink-0" />
            <span>Esta URL deve ser cadastrada exatamente igual no campo {`"Link de redirecionamento"`} do app criado no painel do Bling. Você pode cadastrar múltiplas URLs (preview + produção) no mesmo app.</span>
          </div>
        </div>

        <div>
          <label className="label">Redirect URI Secundária (opcional — ex: preview)</label>
          <input
            className="input font-mono text-xs"
            value={redirectUriAlt}
            onChange={(e) => setRedirectUriAlt(e.target.value)}
            placeholder="https://seu-preview.emergentagent.com/api/bling/callback"
            data-testid="bling-redirect-uri-alt"
          />
          <div className="text-xs text-zinc-500 mt-2 flex items-start gap-2">
            <LinkIcon size={14} className="mt-0.5 flex-shrink-0" />
            <span>Se você usa o mesmo app do Bling em dois ambientes (ex: <strong>produção</strong> e <strong>preview</strong>), cadastre a URL do segundo aqui e o botão {`"Conectar (URL secundária)"`} aparecerá para autenticar por ela. Lembre-se de cadastrar essa URL também no painel do Bling.</span>
          </div>
        </div>

        <button type="submit" className="btn" disabled={saving} data-testid="save-bling-config">
          {saving ? "Salvando..." : "Salvar Configuração"}
        </button>
      </form>

      <div className="mt-6 text-xs text-zinc-500 space-y-1">
        <div className="overline text-zinc-400">DICAS</div>
        <div>· Marque <strong>todos os escopos</strong> ao criar o app no Bling (produtos, pedidos, contatos, NF, financeiro, categorias, depósitos).</div>
        <div>· Sincronização automática roda a cada <strong>2 minutos</strong> (backup). Configure o webhook abaixo para atualização instantânea.</div>
        <div>· Use o botão {`"Sincronizar Agora"`} para forçar uma atualização manual.</div>
        <div>· {`"Re-importar Itens Faltantes"`} busca o detalhe (com os SKUs) de pedidos que vieram sem itens — útil após uma sincronização que tomou rate limit do Bling.</div>
      </div>

      {webhookInfo && (
        <div className="mt-8 border-2 border-black bg-white p-5 md:p-6" data-testid="webhook-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div>
              <div className="overline text-zinc-500">TEMPO REAL</div>
              <h2 className="font-head text-2xl font-black">Webhook do Bling</h2>
            </div>
            <div className="text-xs font-mono bg-zinc-100 px-2 py-1">
              {webhookInfo.total_events} evento{webhookInfo.total_events === 1 ? "" : "s"} recebido{webhookInfo.total_events === 1 ? "" : "s"}
            </div>
          </div>
          <p className="text-sm text-zinc-600 mb-4">
            Cadastre a URL abaixo no painel do Bling em <strong>Preferências → Integrações → Callbacks (Webhooks)</strong>.
            Assim que um pedido é criado, alterado ou cancelado no Bling, você recebe a atualização instantânea aqui — sem esperar o polling de 2 min.
          </p>
          <div className="flex items-stretch gap-2 mb-3">
            <code className="flex-1 border border-black bg-zinc-50 px-3 py-2 font-mono text-xs md:text-sm break-all" data-testid="webhook-url">
              {webhookInfo.webhook_url}
            </code>
            <button onClick={copyWebhookUrl} className="btn btn-ghost px-4" data-testid="webhook-copy">
              {copied ? "COPIADO!" : "COPIAR"}
            </button>
          </div>
          <div className="text-xs text-zinc-500 space-y-1">
            <div>· Método HTTP: <strong className="font-mono">POST</strong></div>
            <div>· Cabeçalho de segurança: <strong className="font-mono">{webhookInfo.signature_header}</strong> (verificação HMAC-SHA256 com seu Client Secret)</div>
            <div>· Assine os eventos de <strong>pedidos de venda</strong>: criação, alteração e cancelamento.</div>
          </div>
          {webhookInfo.recent_events?.length > 0 && (
            <div className="mt-5 border-t border-zinc-200 pt-4">
              <div className="overline text-zinc-400 mb-2">ÚLTIMOS EVENTOS</div>
              <div className="space-y-1 max-h-52 overflow-y-auto">
                {webhookInfo.recent_events.map((ev, i) => (
                  <div key={ev.received_at + "-" + i} className="text-xs font-mono flex items-center justify-between border-b border-zinc-100 py-1 last:border-b-0">
                    <span className="text-zinc-500">{new Date(ev.received_at).toLocaleString("pt-BR")}</span>
                    <span className="px-2 py-0.5 bg-zinc-100">{ev.event_type}</span>
                    <span>bling_id={ev.bling_id || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Push de status: mapa Kanban "Enviado" → Situação Bling */}
      {cfg?.connected && (
        <div className="mt-6 border-2 border-black bg-white p-5 md:p-6" data-testid="status-push-panel">
          <div className="overline text-zinc-500">SINCRONIZAÇÃO INVERSA</div>
          <h2 className="font-head text-2xl font-black mb-2">Status Kanban → Bling</h2>
          <HintButton text="Quando um pedido chega na coluna Enviado do Kanban, o sistema atualiza automaticamente a situação do pedido no Bling. Selecione qual &quot;situação&quot; do Bling representa &quot;Enviado&quot; na sua conta:" />
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <label className="label">Situação Bling equivalente</label>
              <select
                className="input"
                value={enviadoSituacaoId}
                onChange={(e) => setEnviadoSituacaoId(e.target.value)}
                data-testid="enviado-situacao-select"
              >
                <option value="">— Desativado (não enviar ao Bling) —</option>
                {situacoes.map((s) => (
                  <option key={s.id} value={s.id}>{s.nome} (id {s.id}) {s.count > 0 ? `· ${s.count} pedidos` : ""}</option>
                ))}
              </select>
            </div>
            <button
              onClick={saveEnviadoSituacao}
              className="btn"
              disabled={savingSituacao}
              data-testid="save-enviado-situacao"
            >
              {savingSituacao ? "Salvando..." : "Salvar mapeamento"}
            </button>
          </div>
          {cfg.enviado_situacao_id && (
            <div className="mt-3 text-xs text-[#00A300] font-semibold">
              ✓ Ativo: pedidos que forem para "Enviado" no Kanban vão atualizar automaticamente a situação {cfg.enviado_situacao_id} no Bling.
            </div>
          )}
          {statusPushes.length > 0 && (
            <div className="mt-5 border-t border-zinc-200 pt-4">
              <div className="overline text-zinc-400 mb-2">ÚLTIMOS ENVIOS AO BLING</div>
              <div className="space-y-1 max-h-52 overflow-y-auto">
                {statusPushes.map((p, i) => (
                  <div key={p.at + "-" + i} className="text-xs font-mono flex items-center justify-between border-b border-zinc-100 py-1 last:border-b-0" data-testid={`status-push-${i}`}>
                    <span className="text-zinc-500">{new Date(p.at).toLocaleString("pt-BR")}</span>
                    <span>#{p.bling_number}</span>
                    <span className={`px-2 py-0.5 ${p.ok ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                      {p.ok ? "OK" : `Erro ${p.status_code}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {cfg?.connected && (
        <div className="border border-black bg-white p-6 mt-6" data-testid="sync-cutoff-card">
          <div className="overline text-zinc-500 mb-2">SINCRONIZAÇÃO</div>
          <h2 className="font-head font-black text-2xl mb-3">Data de corte do sync</h2>
          <HintButton text="Se definida, o sistema ignora pedidos do Bling com dataEmissao anterior a esta data. Útil para &quot;começar do zero&quot; sem trazer o histórico de novo. Deixe em branco para importar tudo." />
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="label">Sincronizar pedidos a partir de:</label>
              <input
                type="date"
                className="input font-mono"
                value={cutoffDate}
                onChange={(e) => setCutoffDate(e.target.value)}
                data-testid="sync-cutoff-input"
              />
            </div>
            <button
              className="btn"
              onClick={saveCutoffDate}
              disabled={savingCutoff}
              data-testid="save-sync-cutoff"
            >
              {savingCutoff ? "Salvando..." : "Salvar data"}
            </button>
            {cutoffDate && (
              <button
                className="btn btn-ghost"
                onClick={() => { setCutoffDate(""); }}
                data-testid="clear-sync-cutoff"
              >
                Limpar filtro
              </button>
            )}
          </div>
          {cfg.sync_cutoff_date && (
            <div className="mt-3 text-xs text-[#00A300] font-semibold">
              ✓ Ativo: só pedidos com dataEmissao ≥ <span className="font-mono">{cfg.sync_cutoff_date}</span> serão sincronizados.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
